import { collection, query, where, getDocs, getDoc, orderBy, Timestamp, doc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sanitizePostForAggregation } from './blogPostsAggregations';

/**
 * Função para recalcular agregações antigas (backfill)
 * Use esta função uma vez para recalcular todas as agregações históricas
 * 
 * @param {number} startYear - Ano inicial para recalcular (ex: 2020)
 * @param {number} endYear - Ano final para recalcular (ex: 2024)
 * @param {Function} onProgress - Callback para progresso (year, month, processed, total)
 */
export const backfillAggregations = async (startYear = 2020, endYear = new Date().getFullYear(), onProgress = null) => {
  try {
    // Iniciando backfill de agregações
    
    // Buscar tipos de transação para mapear nomes
    const transactionTypesSnapshot = await getDocs(collection(db, 'tiposTransacao'));
    const transactionTypeNamesMap = {};
    transactionTypesSnapshot.forEach(doc => {
      const typeData = doc.data();
      transactionTypeNamesMap[doc.id] = typeData.nome;
    });
    
    // Recalcular agregações de transações
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const startDate = new Date(year, month - 1, 1);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(year, month, 0);
        endDate.setHours(23, 59, 59, 999);
        
        try {
          // Buscar todas as transações do mês
          const transactionsQuery = query(
            collection(db, 'transacoes'),
            where('data', '>=', Timestamp.fromDate(startDate)),
            where('data', '<=', Timestamp.fromDate(endDate)),
            orderBy('data', 'desc')
          );
          
          const transactionsSnapshot = await getDocs(transactionsQuery);
          
          // Inicializar agregação mensal
          const monthlyStats = {
            year,
            month,
            revenue: 0,
            expenses: 0,
            receitaPorTipo: {},
            despesasPorTipo: {},
            receitaPorMaquina: {},
            jogadasPorMaquina: {},
            quantidadeTransacoes: 0,
            quantidadeReceitas: 0,
            quantidadeDespesas: 0,
            nomesTiposTransacao: {},
            detalhamentoDiario: {},
            ultimaAtualizacao: Timestamp.now()
          };
          
          // Processar transações
          transactionsSnapshot.forEach((doc) => {
            const data = doc.data();
            const amount = data.valor || data.amount || 0;
            const transactionDate = (data.data || data.date)?.toDate ? (data.data || data.date).toDate() : new Date(data.data || data.date);
            const day = transactionDate.getDate();
            
            // Inicializar dados diários se necessário
            if (!monthlyStats.detalhamentoDiario[day]) {
              monthlyStats.detalhamentoDiario[day] = {
                revenue: 0,
                expenses: 0,
                quantidadeTransacoes: 0
              };
            }
            
            monthlyStats.quantidadeTransacoes++;
            monthlyStats.detalhamentoDiario[day].quantidadeTransacoes++;
            
            if (data.tipo === 'receita') {
              monthlyStats.revenue += amount;
              monthlyStats.quantidadeReceitas++;
              monthlyStats.detalhamentoDiario[day].revenue += amount;
              
              if (data.idTipoTransacao) {
                monthlyStats.receitaPorTipo[data.idTipoTransacao] = 
                  (monthlyStats.receitaPorTipo[data.idTipoTransacao] || 0) + amount;
                
                // Armazenar nome do tipo se disponível
                if (transactionTypeNamesMap[data.idTipoTransacao]) {
                  monthlyStats.nomesTiposTransacao[data.idTipoTransacao] = transactionTypeNamesMap[data.idTipoTransacao];
                }
              }
              
              if (data.idMaquina) {
                monthlyStats.receitaPorMaquina[data.idMaquina] = 
                  (monthlyStats.receitaPorMaquina[data.idMaquina] || 0) + amount;
                
                if (data.plays) {
                  monthlyStats.jogadasPorMaquina[data.idMaquina] = 
                    (monthlyStats.jogadasPorMaquina[data.idMaquina] || 0) + (Number(data.plays) || 0);
                }
              }
              
            } else if (data.tipo === 'despesa') {
              monthlyStats.expenses += amount;
              monthlyStats.quantidadeDespesas++;
              monthlyStats.detalhamentoDiario[day].expenses += amount;
              
              if (data.idTipoTransacao) {
                monthlyStats.despesasPorTipo[data.idTipoTransacao] = 
                  (monthlyStats.despesasPorTipo[data.idTipoTransacao] || 0) + amount;
                
                // Armazenar nome do tipo se disponível
                if (transactionTypeNamesMap[data.idTipoTransacao]) {
                  monthlyStats.nomesTiposTransacao[data.idTipoTransacao] = transactionTypeNamesMap[data.idTipoTransacao];
                }
              }
            }
          });
          
          // Salvar agregação mensal
          const monthlyStatsRef = doc(db, 'estatisticasMensais', monthKey);
          await setDoc(monthlyStatsRef, monthlyStats);
          
          // Atualizar agregação anual
          const yearlyStatsRef = doc(db, 'estatisticasAnuais', String(year));
          const yearlyStatsDoc = await getDoc(yearlyStatsRef);
          
          const yearlyUpdate = {
            year,
            revenue: 0,
            expenses: 0,
            detalhamentoMensal: {},
            ultimaAtualizacao: Timestamp.now()
          };
          
          // Recalcular agregação anual (buscar todos os meses do ano)
          for (let m = 1; m <= 12; m++) {
            const mKey = `${year}-${String(m).padStart(2, '0')}`;
            const mStatsRef = doc(db, 'estatisticasMensais', mKey);
            const mStatsDoc = await getDoc(mStatsRef);
            
            if (mStatsDoc.exists()) {
              const mStats = mStatsDoc.data();
              const monthRevenue = mStats.revenue || 0;
              yearlyUpdate.revenue += monthRevenue;
              yearlyUpdate.expenses += mStats.expenses || 0;
              yearlyUpdate.detalhamentoMensal[String(m).padStart(2, '0')] = {
                revenue: monthRevenue,
                expenses: mStats.expenses || 0
              };
            } else {
              yearlyUpdate.detalhamentoMensal[String(m).padStart(2, '0')] = {
                revenue: 0,
                expenses: 0
              };
            }
          }
          
          await setDoc(yearlyStatsRef, yearlyUpdate);
          
          const total = (endYear - startYear + 1) * 12;
          const processed = ((year - startYear) * 12) + month;
          
          if (onProgress) {
            onProgress(year, month, processed, total);
          }
          
          // Recalculado mês
        } catch (error) {
          // Erro ao recalcular mês
        }
      }
    }
    
    // Backfill concluído
    return { success: true };
  } catch (error) {
    // Erro no backfill
    return { success: false, error };
  }
};

/**
 * Função para recalcular agregações de um mês específico
 * Útil para corrigir agregações que podem estar desatualizadas
 */
export const recalculateMonthAggregation = async (year, month) => {
  try {
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    const startDate = new Date(year, month - 1, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(year, month, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Buscar todas as transações do mês
    const transactionsQuery = query(
      collection(db, 'transacoes'),
      where('data', '>=', Timestamp.fromDate(startDate)),
      where('data', '<=', Timestamp.fromDate(endDate)),
      orderBy('data', 'desc')
    );
    
    const transactionsSnapshot = await getDocs(transactionsQuery);
    
    // Buscar tipos de transação para mapear nomes
    const transactionTypesSnapshot = await getDocs(collection(db, 'tiposTransacao'));
    const transactionTypeNamesMap = {};
    transactionTypesSnapshot.forEach(doc => {
      const typeData = doc.data();
      transactionTypeNamesMap[doc.id] = typeData.nome;
    });
    
    // Inicializar agregação mensal
    const monthlyStats = {
      year,
      month,
      revenue: 0,
      expenses: 0,
      receitaPorTipo: {},
      despesasPorTipo: {},
      receitaPorMaquina: {},
      jogadasPorMaquina: {},
      quantidadeTransacoes: 0,
      quantidadeReceitas: 0,
      quantidadeDespesas: 0,
      nomesTiposTransacao: {},
      detalhamentoDiario: {},
      ultimaAtualizacao: Timestamp.now()
    };
    
    // Processar transações
    transactionsSnapshot.forEach((doc) => {
      const data = doc.data();
      const amount = data.valor || data.amount || 0;
      const transactionDate = (data.data || data.date)?.toDate ? (data.data || data.date).toDate() : new Date(data.data || data.date);
      const day = transactionDate.getDate();
      
      // Inicializar dados diários se necessário
      if (!monthlyStats.detalhamentoDiario[day]) {
        monthlyStats.detalhamentoDiario[day] = {
          revenue: 0,
          expenses: 0,
          quantidadeTransacoes: 0
        };
      }
      
      monthlyStats.quantidadeTransacoes++;
      monthlyStats.detalhamentoDiario[day].quantidadeTransacoes++;
      
      if (data.tipo === 'receita') {
        monthlyStats.revenue += amount;
        monthlyStats.quantidadeReceitas++;
        monthlyStats.detalhamentoDiario[day].revenue += amount;
        
        if (data.idTipoTransacao) {
          monthlyStats.receitaPorTipo[data.idTipoTransacao] = 
            (monthlyStats.receitaPorTipo[data.idTipoTransacao] || 0) + amount;
          
          // Armazenar nome do tipo se disponível
          if (transactionTypeNamesMap[data.idTipoTransacao]) {
            monthlyStats.nomesTiposTransacao[data.idTipoTransacao] = transactionTypeNamesMap[data.idTipoTransacao];
          }
        }
        
        if (data.idMaquina) {
          monthlyStats.receitaPorMaquina[data.idMaquina] = 
            (monthlyStats.receitaPorMaquina[data.idMaquina] || 0) + amount;
          
          if (data.plays) {
            monthlyStats.jogadasPorMaquina[data.idMaquina] = 
              (monthlyStats.jogadasPorMaquina[data.idMaquina] || 0) + (Number(data.plays) || 0);
          }
        }
        
      } else if (data.tipo === 'despesa') {
        monthlyStats.expenses += amount;
        monthlyStats.quantidadeDespesas++;
        monthlyStats.detalhamentoDiario[day].expenses += amount;
        
        if (data.idTipoTransacao) {
          monthlyStats.despesasPorTipo[data.idTipoTransacao] = 
            (monthlyStats.despesasPorTipo[data.idTipoTransacao] || 0) + amount;
          
          // Armazenar nome do tipo se disponível
          if (transactionTypeNamesMap[data.idTipoTransacao]) {
            monthlyStats.nomesTiposTransacao[data.idTipoTransacao] = transactionTypeNamesMap[data.idTipoTransacao];
          }
        }
      }
    });
    
    // Salvar agregação mensal
    const monthlyStatsRef = doc(db, 'estatisticasMensais', monthKey);
    await setDoc(monthlyStatsRef, monthlyStats);
    
    // Recalculado mês
    return { success: true, stats: monthlyStats };
  } catch (error) {
    // Erro ao recalcular mês
    return { success: false, error };
  }
};

/**
 * Função para popular o agregado de pets inicial
 * Use esta função uma vez para criar o agregado com todos os pets existentes
 */
export const backfillPetsAggregate = async () => {
  try {
    // Iniciando backfill do agregado de pets
    
    // Buscar todos os pets (sem orderBy para evitar erro com campos mistos)
    const petsSnapshot = await getDocs(collection(db, 'pets'));
    const pets = petsSnapshot.docs.map(doc => {
      const data = doc.data();
      // Incluir apenas os campos necessários no agregado
      const petEntry = {
        id: doc.id,
        nome: data.nome || '',
        raca: data.raca || '',
        tutor: data.tutor || '',
        telefone: data.telefone || '',
        criadoEm: data.criadoEm || null,
        atualizadoEm: data.atualizadoEm || null,
        planoMensal: data.planoMensal !== undefined ? data.planoMensal : false
      };
      if (data.galeria != null) petEntry.galeria = data.galeria;
      if (petEntry.planoMensal === true) petEntry.diasAgendados = data.diasAgendados || [];
      
      return petEntry;
    });
    
    // Ordenar por nome no código
    pets.sort((a, b) => {
      const nameA = (a.nome || '').toUpperCase();
      const nameB = (b.nome || '').toUpperCase();
      return nameA.localeCompare(nameB, 'pt-BR');
    });
    
    // Criar ou atualizar o agregado
    const petsAggregateRef = doc(db, 'agregadoPets', 'all');
    await setDoc(petsAggregateRef, {
      pets: pets,
      quantidadeTotal: pets.length,
      ultimaAtualizacao: Timestamp.now()
    });
    
    // Agregado de pets criado
    return { success: true, count: pets.length };
  } catch (error) {
    // Erro ao criar agregado de pets
    return { success: false, error };
  }
};

/**
 * Função para popular os agregados mensais de transações
 * Use esta função uma vez para criar os agregados mensais com todas as transações existentes
 */
export const backfillTransactionsAggregate = async () => {
  try {
    // Iniciando backfill dos agregados mensais de transações
    
    // Buscar todas as transações
    const transactionsQuery = query(
      collection(db, 'transacoes'),
      orderBy('data', 'desc')
    );
    
    // Buscar pets e transactionTypes para incluir nomes no agregado
    const petsAggregateRef = doc(db, 'agregadoPets', 'all');
    const petsAggregateDoc = await getDoc(petsAggregateRef);
    const pets = petsAggregateDoc.exists() ? (petsAggregateDoc.data().pets || []) : [];
    
    const transactionTypesSnapshot = await getDocs(collection(db, 'tiposTransacao'));
    const transactionTypes = transactionTypesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const transactionsSnapshot = await getDocs(transactionsQuery);
    
    // Agrupar transações por mês
    const transactionsByMonth = {};
    
    transactionsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const transactionDate = data.data?.toDate ? data.data.toDate() : new Date(data.data || Timestamp.now());
      const year = transactionDate.getFullYear();
      const month = String(transactionDate.getMonth() + 1).padStart(2, '0');
      const monthKey = `${year}-${month}`;
      
      if (!transactionsByMonth[monthKey]) {
        transactionsByMonth[monthKey] = [];
      }
      
      // Buscar nomePet
      const nomePet = data.idPet 
        ? (pets.find(p => p.id === data.idPet)?.nome || null)
        : null;
      
      // Buscar nomeTipoTransacao
      const nomeTipoTransacao = data.idTipoTransacao
        ? (transactionTypes.find(t => t.id === data.idTipoTransacao)?.nome || null)
        : null;
      
      transactionsByMonth[monthKey].push({
        id: doc.id,
        tipo: data.tipo,
        idPet: data.idPet || null,
        valor: data.valor || 0,
        idTipoTransacao: data.idTipoTransacao || null,
        descricao: data.descricao || '',
        data: data.data || Timestamp.now(), // Se não tem data, usar agora como fallback apenas para criação
        criadoEm: data.criadoEm || null,
        criadoPor: data.criadoPor || null,
        nomePet: nomePet,
        nomeTipoTransacao: nomeTipoTransacao,
        idAgendamento: data.idAgendamento || null
      });
    });
    
    // Criar agregado para cada mês
    let totalTransactions = 0;
    for (const [monthKey, transactions] of Object.entries(transactionsByMonth)) {
      // Ordenar transações do mês por data (mais recente primeiro)
      transactions.sort((a, b) => {
        const dateA = a.data?.toDate ? a.data.toDate() : new Date(a.data);
        const dateB = b.data?.toDate ? b.data.toDate() : new Date(b.data);
        return dateB.getTime() - dateA.getTime();
      });
      
      const [year, month] = monthKey.split('-');
      const transactionsAggregateRef = doc(db, 'agregadoTransacoes', monthKey);
      await setDoc(transactionsAggregateRef, {
        ano: parseInt(year),
        mes: parseInt(month),
        transacoes: transactions,
        quantidadeTotal: transactions.length,
        ultimaAtualizacao: Timestamp.now()
      });
      
      totalTransactions += transactions.length;
    }
    
    // Agregados mensais de transações criados
    return { success: true, count: totalTransactions, months: Object.keys(transactionsByMonth).length };
  } catch (error) {
    // Erro ao criar agregados mensais de transações
    return { success: false, error };
  }
};

/**
 * Função para popular os agregados mensais de agendamentos
 * Use esta função uma vez para criar os agregados mensais com todos os agendamentos existentes
 */
export const backfillAppointmentsAggregate = async () => {
  try {
    // Iniciando backfill dos agregados mensais de agendamentos
    
    // Buscar todos os agendamentos
    const appointmentsSnapshot = await getDocs(collection(db, 'agendamentos'));
    
    // Agrupar agendamentos por mês (formato: YYYY-MM)
    const appointmentsByMonth = {};
    
    appointmentsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      const appointmentDate = data.dataAgendamento;
      
      if (!appointmentDate) {
        return; // Pular agendamentos sem data
      }
      
      const date = appointmentDate?.toDate ? appointmentDate.toDate() : new Date(appointmentDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthKey = `${year}-${month}`;
      
      if (!appointmentsByMonth[monthKey]) {
        appointmentsByMonth[monthKey] = [];
      }
      
      appointmentsByMonth[monthKey].push({
        id: doc.id,
        ...data,
        dataAgendamento: data.dataAgendamento instanceof Date 
          ? Timestamp.fromDate(data.dataAgendamento) 
          : (data.dataAgendamento?.toDate 
              ? data.dataAgendamento 
              : Timestamp.fromDate(new Date(data.dataAgendamento)))
      });
    });
    
    // Criar agregado para cada mês
    let totalAppointments = 0;
    for (const [monthKey, appointments] of Object.entries(appointmentsByMonth)) {
      // Ordenar agendamentos do mês por data (mais recente primeiro)
      appointments.sort((a, b) => {
        const dateA = a.dataAgendamento?.toDate ? a.dataAgendamento.toDate() : new Date(a.dataAgendamento);
        const dateB = b.dataAgendamento?.toDate ? b.dataAgendamento.toDate() : new Date(b.dataAgendamento);
        return dateB.getTime() - dateA.getTime();
      });
      
      const [year, month] = monthKey.split('-');
      const appointmentsAggregateRef = doc(db, 'agregadoAgendamentos', monthKey);
      await setDoc(appointmentsAggregateRef, {
        ano: parseInt(year),
        mes: parseInt(month),
        appointments: appointments,
        quantidadeTotal: appointments.length,
        ultimaAtualizacao: Timestamp.now()
      });
      
      totalAppointments += appointments.length;
    }
    
    // Agregados mensais de agendamentos criados
    return { success: true, count: totalAppointments, months: Object.keys(appointmentsByMonth).length };
  } catch (error) {
    // Erro ao criar agregados mensais de agendamentos
    return { success: false, error };
  }
};

/**
 * Função para atualizar os nomes dos tipos de transação no agregado mensal (monthlyStats)
 * Use esta função para adicionar nomesTiposTransacao aos agregados mensais que não têm
 */
export const updateTransactionTypeNamesInMonthlyStats = async (startYear = 2020, endYear = new Date().getFullYear()) => {
  try {
    // Iniciando atualização dos nomes dos tipos de transação nos agregados mensais
    
    // Buscar tipos de transação
    const transactionTypesSnapshot = await getDocs(collection(db, 'tiposTransacao'));
    const transactionTypeNamesMap = {};
    transactionTypesSnapshot.forEach(doc => {
      const typeData = doc.data();
      transactionTypeNamesMap[doc.id] = typeData.nome;
    });
    
    let totalUpdated = 0;
    let totalProcessed = 0;
    
    // Processar cada mês
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const monthlyStatsRef = doc(db, 'estatisticasMensais', monthKey);
        const monthlyStatsDoc = await getDoc(monthlyStatsRef);
        
        if (!monthlyStatsDoc.exists()) {
          continue;
        }
        
        totalProcessed++;
        const statsData = monthlyStatsDoc.data();
        const needsUpdate = !statsData.nomesTiposTransacao || 
          Object.keys(statsData.nomesTiposTransacao || {}).length === 0;
        
        if (!needsUpdate) {
          // Verificar se todos os tipos que estão em receitaPorTipo/despesasPorTipo têm nomes
          const allTypeIds = new Set();
          if (statsData.receitaPorTipo) {
            Object.keys(statsData.receitaPorTipo).forEach(id => allTypeIds.add(id));
          }
          if (statsData.despesasPorTipo) {
            Object.keys(statsData.despesasPorTipo).forEach(id => allTypeIds.add(id));
          }
          
          const missingNames = Array.from(allTypeIds).filter(id => 
            !statsData.nomesTiposTransacao || !statsData.nomesTiposTransacao[id]
          );
          
          if (missingNames.length === 0) {
            continue; // Já tem todos os nomes
          }
        }
        
        // Atualizar nomesTiposTransacao
        const nomesTiposTransacao = statsData.nomesTiposTransacao || {};
        
        // Adicionar nomes para todos os tipos que aparecem em receitaPorTipo ou despesasPorTipo
        if (statsData.receitaPorTipo) {
          Object.keys(statsData.receitaPorTipo).forEach(typeId => {
            if (!nomesTiposTransacao[typeId] && transactionTypeNamesMap[typeId]) {
              nomesTiposTransacao[typeId] = transactionTypeNamesMap[typeId];
            }
          });
        }
        
        if (statsData.despesasPorTipo) {
          Object.keys(statsData.despesasPorTipo).forEach(typeId => {
            if (!nomesTiposTransacao[typeId] && transactionTypeNamesMap[typeId]) {
              nomesTiposTransacao[typeId] = transactionTypeNamesMap[typeId];
            }
          });
        }
        
        // Atualizar o documento
        await updateDoc(monthlyStatsRef, {
          nomesTiposTransacao: nomesTiposTransacao,
          ultimaAtualizacao: Timestamp.now()
        });
        
        totalUpdated++;
        // Atualizado mês
      }
    }
    
    // Atualização concluída
    return { success: true, updated: totalUpdated, processed: totalProcessed };
  } catch (error) {
    // Erro ao atualizar nomes dos tipos nos agregados mensais
    return { success: false, error };
  }
};


/**
 * Função para popular o agregado de tipos de transação inicial
 * Use esta função uma vez para criar o agregado com todos os tipos de transação existentes
 */
export const backfillTransactionTypesAggregate = async () => {
  try {
    // Iniciando backfill do agregado de tipos de transação
    
    // Buscar todos os tipos de transação
    const transactionTypesQuery = query(
      collection(db, 'tiposTransacao'),
      orderBy('nome', 'asc')
    );
    
    const transactionTypesSnapshot = await getDocs(transactionTypesQuery);
    const transactionTypes = transactionTypesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Criar ou atualizar o agregado
    const transactionTypesAggregateRef = doc(db, 'agregadoTiposTransacao', 'all');
    await setDoc(transactionTypesAggregateRef, {
      tiposTransacao: transactionTypes,
      quantidadeTotal: transactionTypes.length,
      ultimaAtualizacao: Timestamp.now()
    });
    
    // Agregado de tipos de transação criado
    return { success: true, count: transactionTypes.length };
  } catch (error) {
    // Erro ao criar agregado de tipos de transação
    return { success: false, error };
  }
};


/**
 * Função para popular o agregado de posts do blog inicial
 * Use esta função uma vez para criar o agregado com todos os posts existentes
 */
export const backfillBlogPostsAggregate = async () => {
  try {
    // Iniciando backfill do agregado de posts do blog
    
    // Buscar todos os posts do blog
    const blogPostsQuery = query(
      collection(db, 'postsBlog'),
      orderBy('criadoEm', 'desc')
    );
    
    const blogPostsSnapshot = await getDocs(blogPostsQuery);
    const posts = blogPostsSnapshot.docs.map(doc => {
      const data = doc.data();
      const sanitizedData = sanitizePostForAggregation(data);
      return {
        id: doc.id,
        ...sanitizedData,
        criadoEm: data.criadoEm || Timestamp.now(),
        atualizadoEm: data.atualizadoEm || data.criadoEm || Timestamp.now()
      };
    });
    
    // Criar ou atualizar o agregado
    const blogPostsAggregateRef = doc(db, 'agregadoPostsBlog', 'all');
    await setDoc(blogPostsAggregateRef, {
      posts: posts,
      quantidadeTotal: posts.length,
      ultimaAtualizacao: Timestamp.now()
    });
    
    // Agregado de posts do blog criado
    return { success: true, count: posts.length };
  } catch (error) {
    // Erro ao criar agregado de posts do blog
    return { success: false, error };
  }
};

